import React, {useEffect, useState} from "react";
import {Alert, Button, CardContent, FormControl, InputLabel, MenuItem, Select} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import PageLayout from "../components/layout/PageLayout";
import FormFieldLayout from "../components/layout/FormFieldLayout";
import FormControlsLayout from "../components/layout/FormControlsLayout";
import {loginSuccess} from "../store/securitySlice";
import {LOCALE_LABEL_KEY_BY_CODE, normalizeSupportedLocale, SUPPORTED_LOCALES} from "../constants/locales";
import i18n, { markUserLocalePersistedOnServer } from "../i18n";

const SelectLocalePage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [locale, setLocale] = useState(normalizeSupportedLocale(i18n.language, "en"));
    const [email, setEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch("/api/user")
            .then((response) => {
                if (!response.ok) {
                    return Promise.reject();
                }
                return response.json();
            })
            .then((body) => {
                if (!body?.email) {
                    navigate("/signin", {replace: true});
                    return;
                }
                setEmail(body.email);
                if (body.locale) {
                    navigate("/", {replace: true});
                    return;
                }
                setLoading(false);
            })
            .catch(() => {
                setError(t("auth.localeSelection.errors.loadFailed"));
                setLoading(false);
            });
    }, [navigate, t]);

    const handleSubmit = () => {
        if (!SUPPORTED_LOCALES.includes(locale)) {
            setError(t("auth.localeSelection.errors.localeRequired"));
            return;
        }
        setError(null);
        setSaving(true);
        const normalizedLocale = normalizeSupportedLocale(locale, "en");

        fetch("/api/user/locale", {
            method: "PUT",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({locale: normalizedLocale})
        })
            .then((response) => {
                if (!response.ok) {
                    return Promise.reject();
                }
                markUserLocalePersistedOnServer(normalizedLocale);
                dispatch(loginSuccess({email, locale: normalizedLocale}));
                i18n.changeLanguage(normalizedLocale);
                navigate("/", {replace: true});
            })
            .catch(() => {
                setError(t("auth.localeSelection.errors.saveFailed"));
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <PageLayout
            title={t("auth.localeSelection.title")}
            showMenuButton={false}
            showNavigationMenu={false}
            cardStyle={{marginLeft: 330, marginTop: 10, marginRight: 10, marginBottom: 10}}
        >
            <CardContent>
                {error && (
                    <FormFieldLayout>
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    </FormFieldLayout>
                )}
                {!loading && (
                    <>
                        <FormFieldLayout>
                            <FormControl variant="standard" sx={{width: 300}} required disabled={saving}>
                                <InputLabel id="select-locale-label">{t("auth.localeSelection.locale")}</InputLabel>
                                <Select
                                    labelId="select-locale-label"
                                    value={locale}
                                    onChange={(e) => {
                                        setLocale(e.target.value);
                                        setError(null);
                                    }}
                                    label={t("auth.localeSelection.locale")}
                                >
                                    {SUPPORTED_LOCALES.map((localeCode) => (
                                        <MenuItem key={localeCode} value={localeCode}>
                                            {t(LOCALE_LABEL_KEY_BY_CODE[localeCode])}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormFieldLayout>
                        <FormControlsLayout>
                            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                                {saving ? t("auth.localeSelection.saving") : t("auth.localeSelection.continue")}
                            </Button>
                        </FormControlsLayout>
                    </>
                )}
            </CardContent>
        </PageLayout>
    );
};

export default SelectLocalePage;
